import {Dropzone, FileWithPath, IMAGE_MIME_TYPE} from "@mantine/dropzone";
import {Badge, Box, Button, Group, Image, Progress, rem, Table, Text, Alert} from "@mantine/core";
import {IconPhoto, IconUpload, IconX, IconInfoCircle, IconVideo} from "@tabler/icons-react";
import React, {useEffect, useRef} from "react";
import useUploader, {FileStatus} from "./useUploader";
import {timeToString} from "../../../../utils/dateToString";
import useKeepAwake from "../../../../utils/useKeepAwake";
import {installAudioUnlock} from "../../../../utils/files/audioUnlock";

const VIDEO_MIME_TYPES = [
  "video/mp4",
  "video/quicktime",
  "video/x-m4v",
  "video/webm",
  "video/x-matroska",
  "video/x-msvideo",
  "video/3gpp",
  "video/3gpp2"
]
const UPLOAD_MIME_TYPES = [...IMAGE_MIME_TYPE, ...VIDEO_MIME_TYPES]

interface Props {
  galleryID: string
  sharedImages?: File[]
}

function getReadableFileSizeString(fileSizeInBytes: number) {
  var i = -1;
  var byteUnits = [' kB', ' MB', ' GB', ' TB', 'PB', 'EB', 'ZB', 'YB'];
  do {
    fileSizeInBytes /= 1024;
    i++;
  } while (fileSizeInBytes > 1024);

  return Math.max(fileSizeInBytes, 0.1).toFixed(1) + byteUnits[i];
}

const Upload = (props: Props) => {
  const uploader = useUploader(props.galleryID)
  const sharedImagesAddedRef = useRef(false)

  const keepAwake = useKeepAwake()

  // Unlock the AudioContext on the first user interaction so the video
  // transcoder can capture audio without stalling on iOS Safari.
  useEffect(() => {
    installAudioUnlock()
  }, [])

  useEffect(() => {
    if(uploader.stats.waiting > 0 || uploader.stats.processing > 0) {
      keepAwake.requestWakeLock()
    } else {
      keepAwake.releaseWakeLock()
    }
  }, [uploader.stats]);

  const handleDrop = (files: FileWithPath[]) => {
    uploader.addToQueue(files)
  }

  useEffect(() => {
    if (props.sharedImages && props.sharedImages.length > 0 && !sharedImagesAddedRef.current) {
      uploader.addToQueue(props.sharedImages as FileWithPath[])
      sharedImagesAddedRef.current = true
    }
  }, [props.sharedImages, uploader.addToQueue])

  return (
    <>
      {props.sharedImages && props.sharedImages.length > 0 && (
        <Alert variant="light" color="blue" title="Sdílené soubory" icon={<IconInfoCircle/>} mb="md">
          Automaticky bylo přidáno {props.sharedImages.length} souborů ze sdílení.
        </Alert>
      )}

      {
        uploader.stats.total === 0 ? (
          <Dropzone
            onDrop={handleDrop}
            onReject={(files) => console.log('rejected files', files)}
            accept={UPLOAD_MIME_TYPES}
            mt="md"
          >
            <Group justify="center" gap="xl" mih={220} style={{pointerEvents: 'none'}}>
              <Dropzone.Accept>
                <IconUpload
                  style={{width: rem(52), height: rem(52), color: 'var(--mantine-color-blue-6)'}}
                  stroke={1.5}
                />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX
                  style={{width: rem(52), height: rem(52), color: 'var(--mantine-color-red-6)'}}
                  stroke={1.5}
                />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconPhoto
                  style={{width: rem(52), height: rem(52), color: 'var(--mantine-color-dimmed)'}}
                  stroke={1.5}
                />
              </Dropzone.Idle>

              <div>
                <Text size="xl" inline>
                  Přetáhni fotky a videa sem nebo klikni pro výběr souborů
                </Text>
                <Text size="sm" c="dimmed" inline mt={7}>
                  Vyber kolik souborů chceš - celkový limit není omezen
                </Text>
              </div>
            </Group>
          </Dropzone>
        ) : null
      }

      <Progress.Root size="xxl" h={40} mt="xs">
        <Progress.Section value={((uploader.stats.waiting / uploader.stats.total) || 0) * 100} color="gray">
          <Progress.Label>{uploader.stats.waiting} Čeká ve frontě</Progress.Label>
        </Progress.Section>
        <Progress.Section value={((uploader.stats.processing / uploader.stats.total) || 0) * 100} color="orange">
          <Progress.Label>{uploader.stats.processing} Nahrávání</Progress.Label>
        </Progress.Section>
        <Progress.Section value={((uploader.stats.done / uploader.stats.total) || 0)  * 100} color="green" striped animated>
          <Progress.Label>{uploader.stats.done} Nahráno</Progress.Label>
        </Progress.Section>
        <Progress.Section value={((uploader.stats.failed / uploader.stats.total) || 0) * 100} color="red" striped animated>
          <Progress.Label>{uploader.stats.failed} Chyb</Progress.Label>
        </Progress.Section>
      </Progress.Root>

      <Table striped mt="md">
        <Table.Tbody>
          {
            uploader.queue.map((file, index) => (
              <Table.Tr key={index}>
                <Table.Td>
                  <Group>
                    {
                      file.isVideo ? (
                        <Box w={100} h={50} display="flex" style={{alignItems: "center", justifyContent: "center", backgroundColor: "#dee2e6", borderRadius: 4}}>
                          <IconVideo size={28} color="var(--mantine-color-gray-7)"/>
                        </Box>
                      ) : (
                        <Image fit="contain" width={100} height={50} src={file.url}/>
                      )
                    }
                    <Box>
                      <Text>{file.name} {file.isVideo ? <Badge color="grape" ml="xs">Video</Badge> : null}</Text>
                      <Text size="xs">{file.date} {timeToString(file.date)}</Text>
                      {
                        file?.compression ? (
                          <Badge>Komprese {file.compression}% {getReadableFileSizeString(file.size)} - {file.compressionSize ? getReadableFileSizeString(file.compressionSize) : ""}</Badge>
                        ) : null
                      }
                      {
                        file.isVideo && !file.compression ? (
                          <Badge color="gray">{getReadableFileSizeString(file.size)}</Badge>
                        ) : null
                      }
                      {
                        file.status === FileStatus.done ? (
                          <Badge color="green" ml="xs">Nahráno</Badge>
                        ) : null
                      }
                    </Box>
                  </Group>

                  {
                    file.status === FileStatus.idle ? (
                      <Progress.Root size="xxl" h={20} mt="xs">
                        <Progress.Section value={100} color="gray" striped animated>
                          <Progress.Label>Čeká v řadě</Progress.Label>
                        </Progress.Section>
                      </Progress.Root>
                    ) : file.status === FileStatus.heic ? (
                      <Progress.Root size="xxl" h={20} mt="xs">
                        <Progress.Section value={100} color="gray" striped animated>
                          <Progress.Label>Převod z HEIC do jpeg (iphone formát)</Progress.Label>
                        </Progress.Section>
                      </Progress.Root>
                    ) : file.status === FileStatus.compression ? (
                      <Progress.Root size="xxl" h={20} mt="md">
                        <Progress.Section value={100} color="orange" striped animated>
                          <Progress.Label>komprese</Progress.Label>
                        </Progress.Section>
                      </Progress.Root>
                    ) : file.status === FileStatus.videoCompression ? (
                      <Progress.Root size="xxl" h={20} mt="md">
                        <Progress.Section value={file.videoCompressionProgress ?? 0} color="grape" striped animated>
                          <Progress.Label>Komprese videa {file.videoCompressionProgress ?? 0}%</Progress.Label>
                        </Progress.Section>
                      </Progress.Root>
                    ) : file.status === FileStatus.wainingForUpload ? (
                      <Progress.Root size="xxl" h={20} mt="md">
                        <Progress.Section value={100} color="orange" striped>
                          <Progress.Label>Zkompresováno, čeká na nahrání</Progress.Label>
                        </Progress.Section>
                      </Progress.Root>
                    ) : file.status === FileStatus.failed ? (
                      <>
                        <Progress.Root size="xxl" h={20} mt="md">
                          <Progress.Section value={100} color="red">
                            <Progress.Label>Nahrávání selhalo</Progress.Label>
                          </Progress.Section>
                        </Progress.Root>
                        <Button fullWidth onClick={() => uploader.reset(file.key)} size="xs" mt="md">Zkusit znovu</Button>
                      </>
                    ) : file.status === FileStatus.done ? (
                      ""
                    ) : (
                      <>
                        <Progress.Root size="xxl" h={20} mt="xs">
                          <Progress.Section value={file.progress} striped animated>
                            <Progress.Label>{file.progress}% nahrávání</Progress.Label>
                          </Progress.Section>
                        </Progress.Root>
                      </>
                    )
                  }
                </Table.Td>
              </Table.Tr>
            ))
          }
        </Table.Tbody>
      </Table>
    </>
  )
}

export default Upload
